import type { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import axios from "axios";
import type { AuthProvider, AuthProviderConfig } from "@ics/auth-api";

const debugPrefix = "[TestAuthProvider]";
const E2E_TOKEN_ENDPOINT = "/api/e2e/token";

// Syntetisk konto: den isolerte E2E-innloggingen har ingen ekte MSAL-konto, men `currentAccount()` må
// returnere en ikke-null verdi slik at router-guarden ser en aktiv sesjon (jf. `hasActiveSession`).
const syntheticAccount: AccountInfo = {
  homeAccountId: "e2e-test-account",
  environment: "e2e",
  tenantId: "e2e",
  username: "e2e.test@ics-sw.test",
  localAccountId: "e2e-test-account"
};

/**
 * WI 2488 (US 2485): isolert E2E-testinnlogging.
 *
 * Henter et signert test-token fra det gated `/api/e2e/token` (kun tilgjengelig i et Efemert E2E-miljø
 * med signeringsnøkkel — se `EphemeralE2EAuthHandler`) og leverer det som en ekte innlogging ville, slik
 * at `axiosClient` setter `Authorization: Bearer <token>`. Ingen MSAL/Entra involvert; provideren
 * averteres kun av `/api/config` bak dobbeltgaten, så den bygges aldri i test/prod.
 */
export class TestAuthProvider implements AuthProvider {
  readonly id: string;
  readonly displayName: string;
  readonly type = "e2e" as const;

  constructor (config: AuthProviderConfig) {
    this.id = config.id;
    this.displayName = config.displayName;
  }

  async login (): Promise<boolean> {
    return (await this.getTokenSilent()) !== null;
  }

  async loginRedirect (): Promise<void> {
    // E2E-innlogging trenger ingen redirect — token hentes direkte fra backend.
  }

  async handleRedirectResponse (): Promise<string | null> {
    return null;
  }

  async logout (): Promise<void> {
    // Ingen sesjon å rive ned — token er kortlevd og hentes on-demand.
  }

  async isAuthenticated (): Promise<boolean> {
    return (await this.getTokenSilent()) !== null;
  }

  currentAccount (): AccountInfo | null {
    return syntheticAccount;
  }

  async restore (): Promise<boolean> {
    return (await this.getTokenSilent()) !== null;
  }

  async getTokenSilentOrPopup (): Promise<AuthenticationResult | null> {
    return await this.getTokenSilent();
  }

  async getTokenSilent (): Promise<AuthenticationResult | null> {
    try {
      const response = await axios.get<{ accessToken: string }>(E2E_TOKEN_ENDPOINT);
      const accessToken = response.data?.accessToken;
      if (!accessToken) {
        return null;
      }
      // Konsumentene (axiosClient, SignalR) leser kun `accessToken`/`account`; resten av
      // AuthenticationResult er ikke relevant for E2E-token, derfor en minimal cast.
      return { accessToken, account: syntheticAccount } as AuthenticationResult;
    } catch (error) {
      console.warn(`${debugPrefix} Kunne ikke hente E2E-token fra ${E2E_TOKEN_ENDPOINT}:`, error);
      return null;
    }
  }
}
